import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import api from '../services/api';
import {
    Box, Typography, Paper, Grid, Card, CardContent, Tabs, Tab, Table, TableContainer,
    TableHead, TableBody, TableRow, TableCell, Button, TextField, Select, MenuItem,
    FormControl, InputLabel, Chip, CircularProgress, Alert, Fade, Grow,
    LinearProgress, ToggleButtonGroup, ToggleButton, Checkbox, ListSubheader, Divider
} from '@mui/material';
import {
    AccountBalance, CreditCard, Receipt, CloudUpload, Business, Add, Person, FilterList, AutoFixHigh
} from '@mui/icons-material';
import { usePlaidLink } from 'react-plaid-link';
import { TAX_CATEGORIES, TAX_CAT_MAP, TAX_GROUPS } from '../constants/taxCategories';
import { PERSONAL_CATEGORIES, PERSONAL_CAT_MAP, PERSONAL_GROUPS } from '../constants/personalCategories';
import TransactionSearch from './TransactionSearch';
import { dedupePlaidLinkedAccounts } from '../utils/plaidAccountDisplay';

function suggestTaxCategory(tx) {
    const m = ((tx.merchantName || '') + ' ' + (tx.description || '')).toLowerCase();
    const cat = (tx.categoryPrimary || '').toLowerCase();

    // --- Merchant/description keyword matching (most specific first) ---

    // Parking & Tolls
    if (/parking|toll|fastrak|ez.?pass|\bpkg\b/.test(m)) return 'p27';

    // Travel: hotels, flights, rideshare, transit
    if (/marriott|hilton|hyatt|hotel|resort|inn|airbnb|vrbo/.test(m)) return 'p44';
    if (/united\s|american ai|delta\s|southwest|jetblue|spirit\s|alaska air|frontier|inflight|inflt/.test(m)) return 'p44';
    if (/uber\s.*trip|lyft\s|taxi|cab\b/.test(m)) return 'p44';
    if (/bart|clipper|metro|transit|amtrak|caltrain/.test(m)) return 'p44';

    // Subscriptions & software
    if (/adobe|openai|microsoft|google\s*(workspace|cloud)|dropbox|zoom|slack|github|jira|atlassian|canva|mailchimp|squarespace|wix|godaddy|namecheap|apple\.com\/bill|apple\.com\/us|prime\s*video|netflix|hulu|spotify|youtube\s*premium|chatgpt|notion/.test(m)) return 'p14';

    // Telephone
    if (/at&t|t-mobile|tmobile|verizon|sprint|mint\s*mobile|visible|cricket|us\s*cellular|google\s*fi|phone/.test(m)) return 'p41';

    // Internet
    if (/comcast|xfinity|spectrum|cox\s|centurylink|frontier\s*comm|att\s*internet|sonic\.net|starlink/.test(m)) return 'c1';

    // Insurance
    if (/insurance|geico|state\s*farm|allstate|progressive|farmers\s*ins|liberty\s*mutual|travelers|nationwide/.test(m)) return 'p17';

    // Cleaning / janitorial
    if (/homeaglow|maid|janitor|cleaning\s*service|molly\s*maid/.test(m)) return 'p19';

    // Legal & Professional
    if (/attorney|law\s*(office|firm|group)|legal|accountant|cpa|turbotax|h&r\s*block|quickbooks|bookkeep/.test(m)) return 'p21';

    // Office supplies
    if (/staples|office\s*depot|office\s*max/.test(m)) return 'p25';

    // Shipping
    if (/fedex|ups\s|usps|dhl|stamps\.com|ship/.test(m)) return 'p13';

    // Printing
    if (/kinkos|fedex\s*office|printing|print\s*shop/.test(m)) return 'p30';

    // Gas / Auto
    if (/chevron|shell|76\s|arco|exxon|mobil|speedway|7-eleven.*gas|wawa|racetrac|gas\s*mart|76\b|fuel|auto\s*repair|autowerks|jiffy\s*lube|valvoline|pep\s*boys|autozone|o'reilly/.test(m)) return 'p8';

    // Training / education
    if (/udemy|coursera|linkedin\s*learning|pluralsight|skillshare|masterclass|seminar|conference|workshop|training/.test(m)) return 'c3';

    // Donations
    if (/donation|charity|goodwill|salvation\s*army|red\s*cross|unicef|habitat/.test(m)) return 'p52';

    // Bank fees & interest charges
    if (/interest\s*charge|annual\s*membership\s*fee|late\s*fee|plan\s*fee|transaction\s*fee|service\s*charge|nsf/.test(m)) return 'p10';

    // Utilities
    if (/pg&e|pge|pacific\s*gas|edison|water\s*bill|sewer|utility|electric|power/.test(m)) return 'p46';

    // Rent
    if (/rent|lease\s*payment/.test(m)) return 'p31';

    // --- Fall back to bank-provided category ---
    if (cat === 'gas') return 'p8';
    if (cat === 'travel') return 'p44';
    if (cat === 'food & drink') return 'p55';
    if (cat === 'groceries') return 'p55';
    if (cat === 'entertainment') return 'p56';
    if (cat === 'bills & utilities') return 'p46';
    if (cat === 'fees & adjustments') return 'p10';
    if (cat === 'professional services') return 'p21';
    if (cat === 'gifts & donations') return 'p52';
    if (cat === 'home') return 'p24';
    if (cat === 'health & wellness') return 'p15';
    if (cat === 'shopping') return 'p35';
    if (cat === 'personal') return 'p24';

    return 'p24';
}

const PersonalFinance = () => {
    const [tab, setTab] = useState(0);
    const [summary, setSummary] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState(null);

    const [instName, setInstName] = useState('');
    const [acctName, setAcctName] = useState('');
    const [acctType, setAcctType] = useState('credit');
    const [acctMask, setAcctMask] = useState('');
    const [selectedInst, setSelectedInst] = useState('');

    const [uploadAccountId, setUploadAccountId] = useState('');
    const [statementYear, setStatementYear] = useState(new Date().getFullYear());
    const [uploading, setUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [txFilter, setTxFilter] = useState('All');
    const [txAccountFilter, setTxAccountFilter] = useState('All');
    const [txLoading, setTxLoading] = useState(false);
    const [visibleCount, setVisibleCount] = useState(50);
    const [selectedTxIds, setSelectedTxIds] = useState(new Set());
    const [bulkTaxCat, setBulkTaxCat] = useState('');
    const [bulkType, setBulkType] = useState('');
    const [aiLoading, setAiLoading] = useState(false);
    const [plaidLinkToken, setPlaidLinkToken] = useState(null);
    const [plaidConnecting, setPlaidConnecting] = useState(false);
    const [syncingInstId, setSyncingInstId] = useState(null);
    const openedPlaidTokenRef = useRef(null);
    const exchangedPublicTokensRef = useRef(new Set());

    const fetchMeta = useCallback(() => {
        setLoading(true);
        Promise.all([
            api.get(`/api/personal-finance/summary`),
            api.get(`/api/personal-finance/accounts`),
            api.get(`/api/personal-finance/institutions`),
        ]).then(([sumRes, accRes, instRes]) => {
            setSummary(sumRes.data);
            setAccounts(accRes.data);
            setInstitutions(instRes.data);
            if (accRes.data.length > 0 && !uploadAccountId) setUploadAccountId(String(accRes.data[0].id));
            if (instRes.data.length > 0 && !selectedInst) setSelectedInst(String(instRes.data[0].id));
        }).catch(console.error).finally(() => setLoading(false));
    }, [uploadAccountId, selectedInst]);

    const displayAccounts = useMemo(() => {
        const manualAccounts = [];
        const plaidAccounts = [];
        for (const account of accounts) {
            const provider = account.institution?.provider;
            const enriched = {
                ...account,
                institutionName: account.institution?.institutionName || account.institution?.name || '',
            };
            if (provider === 'Plaid') {
                plaidAccounts.push(enriched);
            } else {
                manualAccounts.push(enriched);
            }
        }
        return [...manualAccounts, ...dedupePlaidLinkedAccounts(plaidAccounts)];
    }, [accounts]);

    const selectedTxAccountIds = useMemo(() => {
        if (txAccountFilter === 'All') return [];
        const selected = displayAccounts.find((account) => String(account.id) === String(txAccountFilter));
        if (!selected) return [String(txAccountFilter)];
        const ids = Array.isArray(selected.linkedAccountIds) && selected.linkedAccountIds.length > 0
            ? selected.linkedAccountIds
            : [selected.id];
        return ids.map((id) => String(id));
    }, [displayAccounts, txAccountFilter]);

    const fetchTransactions = useCallback(() => {
        setTxLoading(true);
        const params = selectedTxAccountIds.length === 1 ? { accountId: selectedTxAccountIds[0] } : {};
        api.get(`/api/personal-finance/transactions`, { params })
            .then(res => {
                setTransactions(res.data);
                setVisibleCount(50);
            })
            .catch(console.error)
            .finally(() => setTxLoading(false));
    }, [selectedTxAccountIds]);

    useEffect(() => { fetchMeta(); }, [fetchMeta]);

    useEffect(() => {
        if (tab === 0) fetchTransactions();
    }, [tab, fetchTransactions]);

    useEffect(() => {
        if (displayAccounts.length === 0 || txAccountFilter === 'All') return;
        const selected = displayAccounts.find((account) =>
            Array.isArray(account.linkedAccountIds)
            && account.linkedAccountIds.some((id) => String(id) === String(txAccountFilter))
            && String(account.id) !== String(txAccountFilter),
        );
        if (selected) setTxAccountFilter(String(selected.id));
    }, [displayAccounts, txAccountFilter]);

    useEffect(() => {
        if (displayAccounts.length === 0) return;
        const selectedVisible = displayAccounts.some((account) =>
            String(account.id) === String(uploadAccountId)
            || (
                Array.isArray(account.linkedAccountIds)
                && account.linkedAccountIds.some((id) => String(id) === String(uploadAccountId))
            ),
        );
        if (!uploadAccountId || !selectedVisible) {
            setUploadAccountId(String(displayAccounts[0].id));
        }
    }, [displayAccounts, uploadAccountId]);

    const handleAddInstitution = async (e) => {
        e.preventDefault();
        if (!instName.trim()) return;
        try {
            const { data } = await api.post(`/api/personal-finance/institutions`, {
                institutionName: instName, provider: 'Manual'
            });
            setInstName('');
            setSelectedInst(String(data.id));
            setAlert({ severity: 'success', message: `Added ${data.institutionName}` });
            fetchMeta();
        } catch (err) {
            setAlert({ severity: 'error', message: 'Failed to add institution' });
        }
    };

    const handleAddAccount = async (e) => {
        e.preventDefault();
        if (!acctName.trim() || !selectedInst) return;
        try {
            await api.post(`/api/personal-finance/accounts`, {
                institutionId: selectedInst,
                name: acctName,
                type: acctType,
                subtype: acctType === 'credit' ? 'credit card' : acctType,
                mask: acctMask,
            });
            setAcctName('');
            setAcctMask('');
            setAlert({ severity: 'success', message: `Added account ${acctName}` });
            fetchMeta();
        } catch (err) {
            setAlert({ severity: 'error', message: 'Failed to add account' });
        }
    };

    const handleConnectBank = async () => {
        if (plaidConnecting || plaidLinkToken) return;
        try {
            setAlert(null);
            const { data } = await api.post('/api/personal-finance/plaid/link-token', { userId: 'default-user' });
            setPlaidLinkToken(data.link_token);
        } catch (err) {
            setAlert({ severity: 'error', message: 'Failed to initialize bank connection: ' + (err.response?.data?.error || err.message) });
        }
    };

    const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
        if (!publicToken || exchangedPublicTokensRef.current.has(publicToken)) return;
        exchangedPublicTokensRef.current.add(publicToken);
        setPlaidConnecting(true);
        setAlert(null);
        try {
            await api.post('/api/personal-finance/plaid/exchange-token', {
                publicToken,
                institutionId: metadata.institution?.institution_id || '',
                institutionName: metadata.institution?.name || 'Unknown Institution',
            });
            setAlert({ severity: 'success', message: `Connected ${metadata.institution?.name || 'bank'}! Synced accounts and transactions.` });
            setPlaidLinkToken(null);
            openedPlaidTokenRef.current = null;
            fetchMeta();
            fetchTransactions();
        } catch (err) {
            setAlert({ severity: 'error', message: 'Failed to connect bank: ' + (err.response?.data?.error || err.message) });
        } finally {
            setPlaidConnecting(false);
        }
    }, [fetchMeta, fetchTransactions]);

    const { open: openPlaidLink, ready: plaidReady } = usePlaidLink({
        token: plaidLinkToken,
        onSuccess: onPlaidSuccess,
        onExit: () => {
            setPlaidLinkToken(null);
            openedPlaidTokenRef.current = null;
        },
    });

    useEffect(() => {
        if (!plaidLinkToken) {
            openedPlaidTokenRef.current = null;
            return;
        }
        if (plaidReady && openedPlaidTokenRef.current !== plaidLinkToken) {
            openedPlaidTokenRef.current = plaidLinkToken;
            openPlaidLink();
        }
    }, [plaidLinkToken, plaidReady, openPlaidLink]);

    const handleResyncInstitution = async (instId, instName) => {
        setSyncingInstId(instId);
        setAlert(null);
        try {
            const { data } = await api.post(`/api/personal-finance/plaid/sync/${instId}`);
            setAlert({ severity: 'success', message: `Resynced ${instName}: ${data.accounts || 0} accounts, ${data.transactionsSynced || 0} new transactions` });
            fetchMeta();
            fetchTransactions();
        } catch (err) {
            setAlert({ severity: 'error', message: `Resync failed for ${instName}: ${err.response?.data?.error || err.message}` });
        } finally {
            setSyncingInstId(null);
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!selectedFile || !uploadAccountId) return;
        setUploading(true);
        setAlert(null);
        const fd = new FormData();
        fd.append('accountId', uploadAccountId);
        fd.append('file', selectedFile);
        fd.append('statementYear', statementYear);
        try {
            const { data } = await api.post(`/api/personal-finance/statements/upload`, fd);
            setAlert({
                severity: data.transactionsParsed > 0 ? 'success' : 'info',
                message: data.message
            });
            setSelectedFile(null);
            fetchMeta();
        } catch (err) {
            setAlert({ severity: 'error', message: 'Upload failed: ' + (err.response?.data?.error || err.message) });
        } finally {
            setUploading(false);
        }
    };

    const handleTagTransaction = async (txId, tag) => {
        const tx = transactions.find(t => t.id === txId);
        const suggested = (tag === 'Business' && tx) ? suggestTaxCategory(tx) : null;
        await api.put(`/api/personal-finance/transactions/${txId}/tag`, {
            businessTag: tag, taxCategory: suggested, personalCategory: null
        });
        setTransactions(prev => prev.map(t => t.id === txId
            ? { ...t, businessTag: tag, taxCategory: suggested, personalCategory: null }
            : t));
    };

    const handleTaxCategory = async (txId, catCode) => {
        await api.put(`/api/personal-finance/transactions/${txId}/tag`, {
            businessTag: 'Business', taxCategory: catCode
        });
        setTransactions(prev => prev.map(tx => tx.id === txId
            ? { ...tx, taxCategory: catCode }
            : tx));
    };

    const handlePersonalCategory = async (txId, catCode) => {
        await api.put(`/api/personal-finance/transactions/${txId}/tag`, {
            businessTag: 'Personal', personalCategory: catCode
        });
        setTransactions(prev => prev.map(tx => tx.id === txId
            ? { ...tx, personalCategory: catCode }
            : tx));
    };

    const toggleSelectTx = (id) => setSelectedTxIds(prev => {
        const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
    });

    const handleBulkApply = async () => {
        if ((!bulkTaxCat && !bulkType) || selectedTxIds.size === 0) return;
        const ids = [...selectedTxIds];
        const isBiz = bulkType === 'Business';
        const isPers = bulkType === 'Personal' || (!bulkType && bulkTaxCat);
        const payload = {};
        if (bulkType) payload.businessTag = bulkType;
        if (isBiz && bulkTaxCat) { payload.taxCategory = bulkTaxCat; payload.personalCategory = null; }
        if ((isPers && !isBiz) && bulkTaxCat) { payload.personalCategory = bulkTaxCat; payload.taxCategory = null; }
        if (isBiz && !bulkTaxCat) payload.businessTag = 'Business';
        if (isPers && !isBiz && !bulkTaxCat) payload.businessTag = 'Personal';
        await Promise.all(ids.map(id =>
            api.put(`/api/personal-finance/transactions/${id}/tag`, payload)
        ));
        setTransactions(prev => prev.map(tx => {
            if (!selectedTxIds.has(tx.id)) return tx;
            const updated = { ...tx };
            if (bulkType) updated.businessTag = bulkType;
            if (isBiz && bulkTaxCat) { updated.taxCategory = bulkTaxCat; updated.personalCategory = null; }
            if ((isPers && !isBiz) && bulkTaxCat) { updated.personalCategory = bulkTaxCat; updated.taxCategory = null; }
            return updated;
        }));
        setSelectedTxIds(new Set());
        setBulkTaxCat('');
        setBulkType('');
    };

    const handleAutoCategorize = async () => {
        const ids = selectedTxIds.size > 0
            ? [...selectedTxIds]
            : filteredTransactions.slice(0, visibleCount).map(tx => tx.id);
        const payload = ids.map(id => {
            const tx = transactions.find(t => t.id === id);
            return { id, type: tx?.businessTag || 'Personal' };
        });
        setAiLoading(true);
        try {
            const res = await api.post('/api/personal-finance/auto-categorize', payload);
            const results = res.data.results;
            setTransactions(prev => prev.map(tx => {
                const code = results[tx.id];
                if (!code) return tx;
                if (code.startsWith('h')) return { ...tx, personalCategory: code, taxCategory: null };
                return { ...tx, taxCategory: code, personalCategory: null };
            }));
            setSelectedTxIds(new Set());
        } catch (e) {
            alert('Auto-categorize failed: ' + (e.response?.data?.error || e.message));
        } finally {
            setAiLoading(false);
        }
    };

    const accountFiltered = useMemo(() => {
        if (txAccountFilter === 'All') return transactions;
        const selectedIds = new Set(selectedTxAccountIds);
        return transactions.filter(tx => tx.account && selectedIds.has(String(tx.account.id)));
    }, [transactions, txAccountFilter, selectedTxAccountIds]);

    const businessTxCount = useMemo(() =>
        accountFiltered.filter(tx => tx.businessTag === 'Business').length, [accountFiltered]);

    const personalTxCount = useMemo(() =>
        accountFiltered.filter(tx => !tx.businessTag || tx.businessTag === 'Personal').length, [accountFiltered]);

    const businessTotal = useMemo(() =>
        accountFiltered.filter(tx => tx.businessTag === 'Business')
            .reduce((sum, tx) => sum + Math.abs(Number(tx.amount || 0)), 0), [accountFiltered]);

    const filteredTransactions = useMemo(() => {
        if (txFilter === 'Business') return accountFiltered.filter(tx => tx.businessTag === 'Business');
        if (txFilter === 'Personal') return accountFiltered.filter(tx => !tx.businessTag || tx.businessTag === 'Personal');
        return accountFiltered;
    }, [accountFiltered, txFilter]);

    const StatCard = ({ title, value, icon, gradient }) => (
        <Grow in timeout={600}>
            <Card sx={{
                background: gradient, color: 'white', height: '100%',
                transition: 'transform 0.3s ease', '&:hover': { transform: 'translateY(-4px)' },
                borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }}>
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                            <Typography variant="body2" sx={{ opacity: 0.85, mb: 1, fontWeight: 500 }}>{title}</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>{value}</Typography>
                        </Box>
                        <Box sx={{ opacity: 0.3, fontSize: 48 }}>{icon}</Box>
                    </Box>
                </CardContent>
            </Card>
        </Grow>
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    const years = [];
    for (let y = new Date().getFullYear(); y >= 2020; y--) years.push(y);

    return (
        <Fade in timeout={600}>
            <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, md: 4 } }}>
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e', mb: 0.5 }}>
                        Personal Finance Hub
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        Upload bank &amp; credit card statements — tag transactions as Business for tax deductions
                    </Typography>
                </Box>

                {alert && (
                    <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 3 }}>
                        {alert.message}
                    </Alert>
                )}

                {summary && (
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Accounts" value={displayAccounts.length}
                                icon={<AccountBalance sx={{ fontSize: 48 }} />}
                                gradient="linear-gradient(135deg, #667eea, #764ba2)" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Transactions" value={summary.transactions}
                                icon={<Receipt sx={{ fontSize: 48 }} />}
                                gradient="linear-gradient(135deg, #4facfe, #00f2fe)" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Tagged Business" value={businessTxCount}
                                icon={<Business sx={{ fontSize: 48 }} />}
                                gradient="linear-gradient(135deg, #43e97b, #38f9d7)" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Business Total"
                                value={`$${businessTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
                                icon={<CreditCard sx={{ fontSize: 48 }} />}
                                gradient="linear-gradient(135deg, #f093fb, #f5576c)" />
                        </Grid>
                    </Grid>
                )}

                <TransactionSearch year={statementYear} showDateRange />

                <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)}
                        sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}>
                        <Tab label="Transactions" />
                        <Tab label="Accounts" />
                        <Tab label="Upload Statements" />
                    </Tabs>

                    <Box sx={{ p: 3 }}>
                        {/* ===== TRANSACTIONS TAB ===== */}
                        {tab === 0 && (
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Transactions ({filteredTransactions.length}
                                            {txFilter !== 'All' || txAccountFilter !== 'All' ? ` of ${transactions.length}` : ''})
                                        </Typography>
                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                            <InputLabel>Account</InputLabel>
                                            <Select value={txAccountFilter} label="Account"
                                                onChange={e => setTxAccountFilter(e.target.value)}>
                                                <MenuItem value="All">All Accounts</MenuItem>
                                                {displayAccounts.map(a => (
                                                    <MenuItem key={a.id} value={String(a.id)}>
                                                        {a.name} {a.mask ? `(••${a.mask})` : ''} — {a.type}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <ToggleButtonGroup
                                        value={txFilter}
                                        exclusive
                                        onChange={(_, val) => { if (val) setTxFilter(val); }}
                                        size="small"
                                        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 2 } }}
                                    >
                                        <ToggleButton value="All">
                                            <FilterList sx={{ mr: 0.5, fontSize: 18 }} /> All ({accountFiltered.length})
                                        </ToggleButton>
                                        <ToggleButton value="Personal" sx={{ '&.Mui-selected': { backgroundColor: 'rgba(102,126,234,0.1)', color: '#667eea' } }}>
                                            <Person sx={{ mr: 0.5, fontSize: 18 }} /> Personal ({personalTxCount})
                                        </ToggleButton>
                                        <ToggleButton value="Business" sx={{ '&.Mui-selected': { backgroundColor: 'rgba(67,233,123,0.1)', color: '#2e7d32' } }}>
                                            <Business sx={{ mr: 0.5, fontSize: 18 }} /> Business ({businessTxCount})
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                                {txLoading && <LinearProgress sx={{ mb: 1 }} />}
                                <Box sx={{ mb: 1 }}>
                                    <Button variant="outlined" size="small" onClick={handleAutoCategorize}
                                        disabled={aiLoading}
                                        startIcon={aiLoading ? <CircularProgress size={16} /> : <AutoFixHigh />}
                                        sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#667eea', color: '#667eea',
                                            '&:hover': { borderColor: '#764ba2', color: '#764ba2', backgroundColor: 'rgba(102,126,234,0.04)' } }}>
                                        {aiLoading ? 'Categorizing...' : `AI Categorize${selectedTxIds.size > 0 ? ` (${selectedTxIds.size})` : ' All Visible'}`}
                                    </Button>
                                </Box>
                                {selectedTxIds.size > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, mb: 1,
                                        borderRadius: 2, backgroundColor: '#e3f2fd', border: '1px solid #90caf9', flexWrap: 'wrap' }}>
                                        <Chip label={`${selectedTxIds.size} selected`} color="primary" size="small" sx={{ fontWeight: 700 }} />
                                        <Select size="small" displayEmpty value={bulkType}
                                            onChange={(e) => { setBulkType(e.target.value); setBulkTaxCat(''); }}
                                            sx={{ minWidth: 130, fontSize: '0.85rem', backgroundColor: '#fff' }}>
                                            <MenuItem value=""><em>Keep Tag</em></MenuItem>
                                            <MenuItem value="Personal">Personal</MenuItem>
                                            <MenuItem value="Business">Business</MenuItem>
                                        </Select>
                                        {bulkType && (
                                            <Select size="small" displayEmpty value={bulkTaxCat}
                                                onChange={(e) => setBulkTaxCat(e.target.value)}
                                                sx={{ minWidth: 220, fontSize: '0.85rem', backgroundColor: '#fff' }}
                                                renderValue={(val) => {
                                                    if (!val) return <em>Set Category...</em>;
                                                    const map = bulkType === 'Business' ? TAX_CAT_MAP : PERSONAL_CAT_MAP;
                                                    const c = map[val];
                                                    return c ? `${c.code.toUpperCase()} – ${c.label}` : val;
                                                }}>
                                                <MenuItem value=""><em>Select...</em></MenuItem>
                                                {(bulkType === 'Business' ? TAX_GROUPS : PERSONAL_GROUPS).map(group => [
                                                    <ListSubheader key={group} sx={{ fontSize: '0.75rem', fontWeight: 700, color: bulkType === 'Business' ? '#667eea' : '#e65100', lineHeight: '28px' }}>{group}</ListSubheader>,
                                                    ...(bulkType === 'Business' ? TAX_CATEGORIES : PERSONAL_CATEGORIES).filter(c => c.group === group).map(c => (
                                                        <MenuItem key={c.code} value={c.code} sx={{ fontSize: '0.85rem', pl: 3 }}>{c.code.toUpperCase()} – {c.label}</MenuItem>
                                                    ))
                                                ])}
                                            </Select>
                                        )}
                                        <Button variant="contained" size="small" onClick={handleBulkApply} disabled={!bulkType && !bulkTaxCat}
                                            sx={{ textTransform: 'none', fontWeight: 600,
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                '&:hover': { background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)' } }}>
                                            Apply
                                        </Button>
                                        <Button variant="text" size="small"
                                            onClick={() => { setSelectedTxIds(new Set()); setBulkTaxCat(''); setBulkType(''); }}
                                            sx={{ textTransform: 'none', color: '#666' }}>
                                            Clear
                                        </Button>
                                    </Box>
                                )}
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox size="small"
                                                        checked={filteredTransactions.length > 0 && filteredTransactions.slice(0, visibleCount).every(tx => selectedTxIds.has(tx.id))}
                                                        indeterminate={selectedTxIds.size > 0 && !filteredTransactions.slice(0, visibleCount).every(tx => selectedTxIds.has(tx.id))}
                                                        onChange={() => {
                                                            const visible = filteredTransactions.slice(0, visibleCount);
                                                            const allSel = visible.every(tx => selectedTxIds.has(tx.id));
                                                            setSelectedTxIds(allSel ? new Set() : new Set(visible.map(tx => tx.id)));
                                                        }} />
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Account</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Classification</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredTransactions.slice(0, visibleCount).map(tx => {
                                                const amt = Number(tx.amount);
                                                const isBiz = tx.businessTag === 'Business';
                                                return (
                                                    <TableRow key={tx.id} sx={{
                                                        backgroundColor: selectedTxIds.has(tx.id) ? 'rgba(102,126,234,0.08)' :
                                                            isBiz ? (tx.taxCategory ? 'rgba(67,233,123,0.1)' : 'rgba(255,152,0,0.06)')
                                                            : 'inherit',
                                                        '&:hover': { backgroundColor: '#f5f5f5' }
                                                    }}>
                                                        <TableCell padding="checkbox">
                                                            <Checkbox size="small" checked={selectedTxIds.has(tx.id)}
                                                                onChange={() => toggleSelectTx(tx.id)} />
                                                        </TableCell>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{tx.transactionDate}</TableCell>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                            <Chip label={tx.account ? `${tx.account.name}${tx.account.mask ? ' ••' + tx.account.mask : ''}` : '—'}
                                                                size="small" variant="outlined"
                                                                color={tx.account?.type === 'credit' ? 'warning' : 'info'} />
                                                        </TableCell>
                                                        <TableCell sx={{ maxWidth: 350 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {tx.merchantName || tx.description}
                                                            </Typography>
                                                            {tx.merchantName && tx.description && tx.description !== tx.merchantName && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {tx.description}
                                                                </Typography>
                                                            )}
                                                            {tx.categoryPrimary && (
                                                                <Chip label={tx.categoryPrimary} size="small" variant="outlined"
                                                                    sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} />
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{
                                                            fontWeight: 700,
                                                            color: amt > 0 ? '#e53935' : '#43a047'
                                                        }}>
                                                            {amt > 0 ? '-' : '+'}${Math.abs(amt).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Select size="small" value={tx.businessTag || 'Personal'}
                                                                onChange={(e) => handleTagTransaction(tx.id, e.target.value)}
                                                                sx={{ minWidth: 110, fontSize: '0.85rem' }}>
                                                                <MenuItem value="Personal">Personal</MenuItem>
                                                                <MenuItem value="Business">Business</MenuItem>
                                                            </Select>
                                                        </TableCell>
                                                        <TableCell>
                                                            {isBiz ? (
                                                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                                                    <Select
                                                                        value={tx.taxCategory || ''}
                                                                        displayEmpty
                                                                        onChange={(e) => handleTaxCategory(tx.id, e.target.value)}
                                                                        sx={{
                                                                            fontSize: '0.8rem',
                                                                            backgroundColor: tx.taxCategory ? 'rgba(67,233,123,0.08)' : 'rgba(255,152,0,0.08)',
                                                                            '& .MuiSelect-select': { py: 0.7 }
                                                                        }}
                                                                        renderValue={(val) => {
                                                                            if (!val) return <em style={{ color: '#999' }}>Select tax category...</em>;
                                                                            const c = TAX_CAT_MAP[val];
                                                                            return c ? `${c.code} ${c.label}` : val;
                                                                        }}
                                                                    >
                                                                        <MenuItem value="" sx={{ color: '#999' }}><em>Uncategorized</em></MenuItem>
                                                                        {TAX_GROUPS.map(group => [
                                                                            <MenuItem key={`hdr-${group}`} disabled
                                                                                sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase',
                                                                                    color: '#1a237e', backgroundColor: '#f0f0f0', opacity: '1 !important' }}>
                                                                                {group}
                                                                            </MenuItem>,
                                                                            ...TAX_CATEGORIES.filter(c => c.group === group).map(c => (
                                                                                <MenuItem key={c.code} value={c.code} sx={{ fontSize: '0.85rem', pl: 3 }}>
                                                                                    <Box component="span" sx={{ color: '#666', mr: 1, fontFamily: 'monospace', fontSize: '0.75rem' }}>{c.code}</Box>
                                                                                    {c.label}
                                                                                </MenuItem>
                                                                            ))
                                                                        ])}
                                                                    </Select>
                                                                </FormControl>
                                                            ) : (
                                                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                                                    <Select
                                                                        value={tx.personalCategory || ''}
                                                                        displayEmpty
                                                                        onChange={(e) => handlePersonalCategory(tx.id, e.target.value)}
                                                                        sx={{
                                                                            fontSize: '0.8rem',
                                                                            backgroundColor: tx.personalCategory ? 'rgba(230,81,0,0.08)' : 'transparent',
                                                                            '& .MuiSelect-select': { py: 0.7 }
                                                                        }}
                                                                        renderValue={(val) => {
                                                                            if (!val) return <em style={{ color: '#999' }}>Select category...</em>;
                                                                            const c = PERSONAL_CAT_MAP[val];
                                                                            return c ? `${c.label}` : val;
                                                                        }}
                                                                    >
                                                                        <MenuItem value="" sx={{ color: '#999' }}><em>Uncategorized</em></MenuItem>
                                                                        {PERSONAL_GROUPS.map(group => [
                                                                            <MenuItem key={`hdr-${group}`} disabled
                                                                                sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase',
                                                                                    color: '#e65100', backgroundColor: '#fff3e0', opacity: '1 !important' }}>
                                                                                {group}
                                                                            </MenuItem>,
                                                                            ...PERSONAL_CATEGORIES.filter(c => c.group === group).map(c => (
                                                                                <MenuItem key={c.code} value={c.code} sx={{ fontSize: '0.85rem', pl: 3 }}>
                                                                                    {c.label}
                                                                                </MenuItem>
                                                                            ))
                                                                        ])}
                                                                    </Select>
                                                                </FormControl>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                            {filteredTransactions.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                                        {transactions.length === 0
                                                            ? 'No transactions yet. Add an account and upload a statement to get started.'
                                                            : `No ${txFilter.toLowerCase()} transactions found.`}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {filteredTransactions.length > visibleCount && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                        <Button variant="outlined" onClick={() => setVisibleCount(prev => prev + 50)}
                                            sx={{ borderRadius: 2, textTransform: 'none', px: 4 }}>
                                            Load More ({filteredTransactions.length - visibleCount} remaining)
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* ===== ACCOUNTS TAB ===== */}
                        {tab === 1 && (
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined" sx={{ borderRadius: 2, p: 3, mb: 3,
                                        background: 'linear-gradient(135deg, #f0f4ff 0%, #f8f0ff 100%)',
                                        borderColor: '#667eea' }}>
                                        <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                                            <AccountBalance sx={{ color: '#667eea', fontSize: 28 }} />
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                Connect Bank via Plaid
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Securely link your bank accounts and credit cards to automatically import transactions.
                                        </Typography>
                                        <Button variant="contained" onClick={handleConnectBank}
                                            disabled={plaidConnecting || Boolean(plaidLinkToken)}
                                            startIcon={plaidConnecting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <AccountBalance />}
                                            sx={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', borderRadius: 2, py: 1.2,
                                                fontWeight: 600, textTransform: 'none', fontSize: '0.95rem' }}>
                                            {plaidConnecting ? 'Connecting...' : 'Connect a Bank Account'}
                                        </Button>
                                    </Card>

                                    <Divider sx={{ my: 2 }}>
                                        <Chip label="OR add manually" size="small" sx={{ fontWeight: 600, color: '#888' }} />
                                    </Divider>

                                    <Card variant="outlined" sx={{ borderRadius: 2, p: 3, mb: 3 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                            Step 1: Add Institution
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Add the bank or credit card company (e.g. Bank of America, Chase, Amex)
                                        </Typography>
                                        <form onSubmit={handleAddInstitution} style={{ display: 'flex', gap: 12 }}>
                                            <TextField fullWidth size="small" label="Institution Name" value={instName}
                                                onChange={e => setInstName(e.target.value)} required />
                                            <Button type="submit" variant="contained" startIcon={<Add />}
                                                sx={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', whiteSpace: 'nowrap', borderRadius: 2 }}>
                                                Add
                                            </Button>
                                        </form>
                                    </Card>

                                    {institutions.length > 0 && (
                                        <Card variant="outlined" sx={{ borderRadius: 2, p: 3 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                                Step 2: Add Account
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                Add a specific account under an institution (e.g. Visa ending 4242)
                                            </Typography>
                                            <form onSubmit={handleAddAccount}>
                                                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                                    <InputLabel>Institution</InputLabel>
                                                    <Select value={selectedInst} label="Institution"
                                                        onChange={e => setSelectedInst(e.target.value)}>
                                                        {institutions.map(i => (
                                                            <MenuItem key={i.id} value={String(i.id)}>{i.institutionName}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                <TextField fullWidth size="small" label="Account Name" value={acctName}
                                                    placeholder="e.g. Visa Platinum, Checking" sx={{ mb: 2 }}
                                                    onChange={e => setAcctName(e.target.value)} required />
                                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                                    <Grid item xs={6}>
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>Type</InputLabel>
                                                            <Select value={acctType} label="Type"
                                                                onChange={e => setAcctType(e.target.value)}>
                                                                <MenuItem value="credit">Credit Card</MenuItem>
                                                                <MenuItem value="checking">Checking</MenuItem>
                                                                <MenuItem value="savings">Savings</MenuItem>
                                                                <MenuItem value="investment">Investment</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <TextField fullWidth size="small" label="Last 4 Digits" value={acctMask}
                                                            onChange={e => setAcctMask(e.target.value)} placeholder="4242" inputProps={{ maxLength: 4 }} />
                                                    </Grid>
                                                </Grid>
                                                <Button type="submit" variant="contained" fullWidth startIcon={<Add />}
                                                    sx={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', borderRadius: 2, py: 1.2 }}>
                                                    Add Account
                                                </Button>
                                            </form>
                                        </Card>
                                    )}
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined" sx={{ borderRadius: 2, p: 3 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Your Accounts</Typography>
                                        {displayAccounts.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                No accounts yet. Add an institution first, then add an account.
                                            </Typography>
                                        ) : (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {institutions.map(inst => {
                                                    const instAccounts = displayAccounts.filter(a => a.institution?.id === inst.id);
                                                    if (instAccounts.length === 0) return null;
                                                    const isPlaid = inst.provider === 'Plaid';
                                                    return (
                                                        <Card key={inst.id} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                                            <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                                backgroundColor: isPlaid ? '#f0f4ff' : '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                                                                <Box display="flex" alignItems="center" gap={1}>
                                                                    <AccountBalance sx={{ fontSize: 18, color: isPlaid ? '#667eea' : '#888' }} />
                                                                    <Typography variant="subtitle2" fontWeight={700}>{inst.institutionName}</Typography>
                                                                    {isPlaid && <Chip label="Plaid" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, backgroundColor: '#e8eaf6', color: '#3949ab' }} />}
                                                                </Box>
                                                                {isPlaid && (
                                                                    <Button size="small" onClick={() => handleResyncInstitution(inst.id, inst.institutionName)}
                                                                        disabled={syncingInstId === inst.id}
                                                                        startIcon={syncingInstId === inst.id ? <CircularProgress size={14} /> : <Receipt />}
                                                                        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}>
                                                                        {syncingInstId === inst.id ? 'Syncing...' : 'Resync'}
                                                                    </Button>
                                                                )}
                                                            </Box>
                                                            <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                {instAccounts.map(acct => (
                                                                    <Box key={acct.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                                        p: 1, borderRadius: 1, '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' } }}>
                                                                        <Box>
                                                                            <Typography variant="body2" fontWeight={600}>{acct.name}</Typography>
                                                                            {acct.officialName && <Typography variant="caption" color="text.secondary">{acct.officialName}</Typography>}
                                                                        </Box>
                                                                        <Box display="flex" alignItems="center" gap={1}>
                                                                            <Chip label={acct.type} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                                                                            {acct.mask && <Typography variant="caption" color="text.secondary">•••• {acct.mask}</Typography>}
                                                                        </Box>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        </Card>
                                                    );
                                                })}
                                            </Box>
                                        )}
                                    </Card>
                                </Grid>
                            </Grid>
                        )}

                        {/* ===== UPLOAD STATEMENTS TAB ===== */}
                        {tab === 2 && (
                            <Box sx={{ maxWidth: 620 }}>
                                <Card variant="outlined" sx={{ borderRadius: 2, p: 3 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Upload Statement</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        Upload a bank or credit card statement (PDF or CSV). Transactions will be
                                        automatically extracted. CSV exports from your bank's website work best.
                                    </Typography>

                                    {displayAccounts.length === 0 ? (
                                        <Alert severity="warning">
                                            Add an institution and account in the Accounts tab first before uploading statements.
                                        </Alert>
                                    ) : (
                                        <form onSubmit={handleUpload}>
                                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                                <Grid item xs={8}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Account</InputLabel>
                                                        <Select value={uploadAccountId} label="Account"
                                                            onChange={e => setUploadAccountId(e.target.value)}>
                                                            {displayAccounts.map(a => (
                                                                <MenuItem key={a.id} value={String(a.id)}>
                                                                    {a.name} {a.mask ? `(••${a.mask})` : ''}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Year</InputLabel>
                                                        <Select value={statementYear} label="Year"
                                                            onChange={e => setStatementYear(e.target.value)}>
                                                            {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>

                                            <Button variant="outlined" component="label" fullWidth startIcon={<CloudUpload />}
                                                sx={{ mb: 2, py: 2, borderStyle: 'dashed', borderWidth: 2 }}>
                                                {selectedFile ? selectedFile.name : 'Select PDF, CSV, or Excel Statement'}
                                                <input type="file" accept=".pdf,.csv,.xlsx,.xls" hidden
                                                    onChange={e => setSelectedFile(e.target.files[0] || null)} />
                                            </Button>

                                            {uploading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

                                            <Button type="submit" variant="contained" fullWidth disabled={!selectedFile || uploading}
                                                sx={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', borderRadius: 2, py: 1.3, fontWeight: 600 }}>
                                                {uploading ? 'Processing...' : 'Upload & Parse Transactions'}
                                            </Button>
                                        </form>
                                    )}
                                </Card>

                                <Card variant="outlined" sx={{ borderRadius: 2, p: 3, mt: 3 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Supported Banks</Typography>
                                    <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 2 }}>
                                        The parser auto-detects your bank from CSV headers or PDF content:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                        {['Bank of America', 'American Express', 'Chase', 'Citi', 'Wells Fargo'].map(b => (
                                            <Chip key={b} label={b} size="small" color="primary" variant="outlined" />
                                        ))}
                                    </Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Tips</Typography>
                                    <Typography variant="body2" color="text.secondary" component="div">
                                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                                            <li><strong>CSV is preferred</strong> — download from your bank's Activity page for the most accurate parsing.</li>
                                            <li><strong>PDF statements</strong> work too, but CSV gives better results.</li>
                                            <li><strong>Statement year</strong> — used for dates without a year (e.g. "01/15").</li>
                                            <li><strong>Sign conventions</strong> are handled per bank (e.g. Amex positive = charges, Chase negative = charges).</li>
                                        </ul>
                                    </Typography>
                                </Card>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Fade>
    );
};

export default PersonalFinance;
